import { AppServiceitemsSlideover } from '#components'
import { z } from '#netzo/shared/utils/zod'
import type { DropdownItem } from '#ui/types'
import type { ComponentProps } from 'vue-component-type-helpers'

// types:

type ServiceitemitemsSlideoverProps = ComponentProps<typeof AppServiceitemsSlideover>

export type ServiceitemsUtils = ReturnType<typeof useServiceitemsUtils>

export const useServiceitems = () => {
  const {
    onSubmitCreate,
    onSubmitUpdate,
    onSubmitDelete,
    onSubmitUpdateMultiple,
    slideover,
    modal,
    slideoverOpenInformation,
    slideoverOpenFilePreview,
    slideoverOpenFilePreviewPdf,
    modalOpenDelete,
    modalOpenShareFile,
  } = useRows<Serviceitem>('serviceitems')

  const slideoverOpenCreate = (props: AppServiceitemsSlideoverProps) => {
    return new Promise<Serviceitem>((resolve, reject) => {
      const data = getDefaultServiceitem(props.data)
      slideover.open(AppServiceitemsSlideover, {
        title: 'Crear item',
        ...props,
        data,
        onSubmit: props?.onSubmit ?? (async (event) => {
          try {
            const result = await onSubmitCreate(event.data)
            resolve(result)
          }
          catch (error) {
            reject(error)
          }
          finally {
            slideover.close() // ensures slideover is always closed
          }
        }),
        action: 'create',
        openNestedCallback: slideoverOpenCreate,
      })
    })
  }

  const slideoverOpenEdit = (props: AppServiceitemsSlideoverProps) => {
    return new Promise<Serviceitem>((resolve, reject) => {
      const data = getDefaultServiceitem(props.data)
      slideover.open(AppServiceitemsSlideover, {
        title: 'Editar item',
        ...props,
        data,
        disabledFields: ['serviceId'],
        onSubmit: props?.onSubmit ?? (async (event) => {
          try {
            const result = await onSubmitUpdate(event.data)
            resolve(result)
          }
          catch (error) {
            reject(error)
          }
          finally {
            slideover.close() // ensures slideover is always closed
          }
        }),
        action: 'edit',
        openNestedCallback: slideoverOpenEdit,
      })
    })
  }

  const schema = z.object({
    name: z.string(),
    fiscalData: fiscalDataSchema,
    serviceId: z.string(),
  })

  // actions:

  const getDropdownItems = (row: Serviceitem, refresh: () => Promise<void>, utils: ServiceitemsUtils, dropdownItemsAction: DropdownItem[] = []): DropdownItem[][] => [
    [
      getDropdownItem('editOrView', 'serviceitems', row, refresh, utils),
    ],
    [
      getDropdownItem('copyId', 'serviceitems', row, refresh, utils),
    ],
    [
      getDropdownItem('notes', 'serviceitems', row, refresh, utils),
      getDropdownItem('files', 'serviceitems', row, refresh, utils),
    ],
    [
      ...dropdownItemsAction,
    ],
    [
      getDropdownItem('info', 'serviceitems', row, refresh, utils),
      {
        label: row.immutable ? 'Desbloquear' : 'Bloquear',
        icon: row.immutable ? 'i-mdi-lock-open-variant' : 'i-mdi-lock',
        disabled: row?.service?.immutable,
        click: async () => {
          await onSubmitUpdate({ ...row, immutable: !row.immutable })
          await refresh()
        },
      },
    ],
    [
      getDropdownItem('archive', 'serviceitems', row, refresh, utils),
      getDropdownItem('delete', 'serviceitems', row, refresh, utils),
    ],
  ].filter(items => items?.length)

  return {
    onSubmitCreate,
    onSubmitUpdate,
    onSubmitDelete,
    onSubmitUpdateMultiple,
    slideover,
    modal,
    slideoverOpenInformation,
    slideoverOpenFilePreview,
    slideoverOpenFilePreviewPdf,
    modalOpenDelete,
    modalOpenShareFile,
    slideoverOpenCreate,
    slideoverOpenEdit,
    schema,
    getDropdownItems,
  }
}

export const useServiceitemsUtils = ({ $services }: {
  $services?: Awaited<ReturnType<typeof useFetch<Service[]>>>
} = {}) => {
  const options = {
    ...optionsServiceitems,
    fiscalData: {
      satUnitKey: optionsSAT.productUnitId,
    },
    serviceId: computed(() => $services?.data.value.map(toOption)),
  }

  const maps = {
    fiscalData: {
      satUnitKey: toMapByKey(optionsSAT.productUnitId, 'value'),
    },
    service: {
      type: toMapByKey(optionsServices.type, 'value'),
    },
    serviceId: computed(() => toMapByKey($services?.data.value, 'id')),
    servicesByType: computed(() => toMapByGroupKey($services?.data.value, 'type')),
  }

  return { options, maps }
}
